import {
  Button,
  Card,
  CardContent,
  Input,
  Label,
  toast,
} from '@sitewise/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useId, useMemo, useState, type FormEvent } from 'react';

import {
  createUnitLeadWorkEvent,
  deleteUnitLeadWorkEvent,
  updateUnitLeadWorkEvent,
} from '../../../../api/lead.js';
import { ApiError } from '../../../../lib/api-client.js';

import type { LeadWorkEvent } from '../../../../api/lead.js';


interface Props {
  unitId: string;
  events: LeadWorkEvent[];
  disabled?: boolean;
}

/**
 * Reverse-chronological list of `unit_lead_work_events` with an
 * inline "Add work event" composer at the top. Rows are read-only by
 * default; hover surfaces edit + delete actions for staff who can
 * modify them.
 */
export function WorkEventsList({ unitId, events, disabled }: Props) {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['unit-lead', unitId] });
  };

  const deleteMutation = useMutation({
    mutationFn: (eventId: string) => deleteUnitLeadWorkEvent(unitId, eventId),
    onSuccess: invalidate,
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not remove work event'),
  });

  return (
    <Card>
      <CardContent className="space-y-3 p-5">
        <div>
          <h3 className="text-sm font-semibold">Work done</h3>
          <p className="text-xs text-fg-muted">
            Dated log of remediation, encapsulation, and follow-up work.
          </p>
        </div>

        <AddWorkEventForm unitId={unitId} disabled={disabled} onSubmitted={invalidate} />

        {events.length === 0 ? (
          <p className="rounded-md border border-dashed border-border bg-surface-muted/40 p-4 text-sm text-fg-muted">
            No work logged for this unit yet.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {events.map((evt) => (
              <li key={evt.id} className="group py-3">
                {editingId === evt.id ? (
                  <EditWorkEventForm
                    unitId={unitId}
                    event={evt}
                    onDone={() => {
                      setEditingId(null);
                      invalidate();
                    }}
                    onCancel={() => setEditingId(null)}
                  />
                ) : (
                  <WorkEventRow
                    event={evt}
                    disabled={disabled}
                    onEdit={() => setEditingId(evt.id)}
                    onDelete={() => {
                      if (confirm(`Remove this work event from ${evt.performedOn}?`)) {
                        deleteMutation.mutate(evt.id);
                      }
                    }}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function WorkEventRow({
  event,
  disabled,
  onEdit,
  onDelete,
}: {
  event: LeadWorkEvent;
  disabled?: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-xs text-fg-muted">
          <time dateTime={event.performedOn} className="tabular-nums font-medium text-fg">
            {event.performedOn}
          </time>
          {event.contractor ? (
            <span className="truncate" title={event.contractor}>
              · {event.contractor}
            </span>
          ) : null}
          {event.createdByName ? (
            <span className="truncate" title={`Logged by ${event.createdByName}`}>
              · {event.createdByName}
            </span>
          ) : null}
        </div>
        <p className="mt-1 whitespace-pre-wrap text-sm text-fg">{event.description}</p>
        {event.documents.length > 0 ? (
          <ul className="mt-1.5 flex flex-wrap gap-1.5">
            {event.documents.map((d) => (
              <li
                key={d.id}
                className="inline-flex max-w-[200px] items-center gap-1 truncate rounded bg-surface-muted px-1.5 py-0.5 text-xs text-fg-secondary"
                title={d.originalFilename}
              >
                <span className="truncate">{d.originalFilename}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {!disabled ? (
        <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          <Button size="icon" variant="ghost" aria-label="Edit" onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5 text-fg-muted" />
          </Button>
          <Button size="icon" variant="ghost" aria-label="Remove" onClick={onDelete}>
            <Trash2 className="h-3.5 w-3.5 text-fg-muted" />
          </Button>
        </div>
      ) : null}
    </div>
  );
}

interface ComposerProps {
  unitId: string;
  disabled?: boolean;
  onSubmitted: () => void;
}

function AddWorkEventForm({ unitId, disabled, onSubmitted }: ComposerProps) {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const dateId = useId();
  const descId = useId();
  const contractorId = useId();

  const [open, setOpen] = useState(false);
  const [performedOn, setPerformedOn] = useState(today);
  const [description, setDescription] = useState('');
  const [contractor, setContractor] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      createUnitLeadWorkEvent(unitId, {
        performed_on: performedOn,
        description: description.trim(),
        contractor: contractor.trim() || null,
        document_ids: [],
      }),
    onSuccess: () => {
      setOpen(false);
      setDescription('');
      setContractor('');
      setPerformedOn(today);
      toast.success('Work event added.');
      onSubmitted();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not add work event');
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!description.trim()) {
      toast.error('Description is required.');
      return;
    }
    mutation.mutate();
  }

  if (!open) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        <Plus className="h-3.5 w-3.5" />
        Add work event
      </Button>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-3 rounded-md border border-border bg-surface-muted/30 p-3"
    >
      <div className="grid gap-2 sm:grid-cols-[160px_1fr]">
        <div className="space-y-1">
          <Label htmlFor={dateId} className="text-xs">
            Date
          </Label>
          <Input
            id={dateId}
            type="date"
            value={performedOn}
            onChange={(e) => setPerformedOn(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={descId} className="text-xs">
            Description
          </Label>
          <textarea
            id={descId}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="What was done?"
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor={contractorId} className="text-xs">
          Contractor (optional)
        </Label>
        <Input
          id={contractorId}
          value={contractor}
          onChange={(e) => setContractor(e.target.value)}
          placeholder="e.g. Acme Lead Services"
        />
      </div>
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setOpen(false);
            setDescription('');
            setContractor('');
          }}
        >
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={mutation.isPending}>
          {mutation.isPending ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </form>
  );
}

interface EditProps {
  unitId: string;
  event: LeadWorkEvent;
  onDone: () => void;
  onCancel: () => void;
}

function EditWorkEventForm({ unitId, event, onDone, onCancel }: EditProps) {
  const dateId = useId();
  const descId = useId();
  const contractorId = useId();
  const [performedOn, setPerformedOn] = useState(event.performedOn);
  const [description, setDescription] = useState(event.description);
  const [contractor, setContractor] = useState(event.contractor ?? '');

  const mutation = useMutation({
    mutationFn: () =>
      updateUnitLeadWorkEvent(unitId, event.id, {
        performed_on: performedOn,
        description: description.trim(),
        contractor: contractor.trim() || null,
      }),
    onSuccess: () => {
      toast.success('Work event updated.');
      onDone();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not update work event');
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!description.trim()) {
      toast.error('Description is required.');
      return;
    }
    mutation.mutate();
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-3 rounded-md border border-border bg-surface-muted/40 p-3"
    >
      <div className="grid gap-2 sm:grid-cols-[160px_1fr]">
        <div className="space-y-1">
          <Label htmlFor={dateId} className="text-xs">
            Date
          </Label>
          <Input
            id={dateId}
            type="date"
            value={performedOn}
            onChange={(e) => setPerformedOn(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={descId} className="text-xs">
            Description
          </Label>
          <textarea
            id={descId}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor={contractorId} className="text-xs">
          Contractor
        </Label>
        <Input
          id={contractorId}
          value={contractor}
          onChange={(e) => setContractor(e.target.value)}
        />
      </div>
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
